import { useCallback, useEffect, useRef, useState } from "react";

import {
  dispatchVoiceDictationShortcut,
  isVoiceDictationShortcut,
} from "@/hooks/use-voice-dictation-shortcut";

function hasOpenRadixDialog(): boolean {
  return document.querySelector("[role='dialog'][data-state='open']") !== null;
}

function getActiveElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const activeElement = document.activeElement;
  return activeElement instanceof HTMLElement ? activeElement : null;
}

function isQuickLaunchOpenShortcut(
  event: Pick<KeyboardEvent, "code" | "ctrlKey" | "metaKey" | "repeat">,
): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    event.code === "Slash" &&
    !event.repeat
  );
}

/**
 * Global Cmd/Ctrl+/ quick-launch opening shortcut state.
 */
export function useQuickLaunch() {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  // Quick launch is opened outside a Radix DialogTrigger. Remembering its
  // invoker gives DialogContent a reliable APG-compliant focus-return target.
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const open = useCallback(() => {
    if (!isOpenRef.current) returnFocusRef.current = getActiveElement();
    isOpenRef.current = true;
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);
  const toggle = useCallback(() => {
    const nextOpen = !isOpenRef.current;
    if (nextOpen) returnFocusRef.current = getActiveElement();
    isOpenRef.current = nextOpen;
    setIsOpen(nextOpen);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // A focused chat textarea consumes Cmd/Ctrl+E to start its own voice
      // recorder. Its preventDefault reaches this window listener, which
      // defers to the composer that handled the shortcut.
      if (event.defaultPrevented) return;

      if (isOpenRef.current) {
        if (!isVoiceDictationShortcut(event)) return;
        event.preventDefault();
        dispatchVoiceDictationShortcut("quick-launch");
        return;
      }

      if (!isQuickLaunchOpenShortcut(event)) return;
      if (hasOpenRadixDialog()) return;

      event.preventDefault();
      open();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  return { isOpen, open, close, toggle, returnFocusRef };
}
